// ============================================
// CoinBadge — collectible coin visual
// Tiers: gold | silver | bronze
// ============================================
import styles from './CoinBadge.module.css';
import { SignatureBadge } from '../CoinMedia/CoinMedia';
import { resolveCoinMedia } from '../../utils/coinMedia';

const rarityConfig = {
    gold: {
        color: '#d4af37',
        glow: 'rgba(212, 175, 55, 0.5)',
        label: 'Gold',
    },
    silver: {
        color: '#c0c0c0',
        glow: 'rgba(192, 192, 192, 0.5)',
        label: 'Silver',
    },
    bronze: {
        color: '#cd7f32',
        glow: 'rgba(205, 127, 50, 0.5)',
        label: 'Bronze',
    },
    legendary: {
        color: '#9d4edd',
        glow: 'rgba(157, 78, 221, 0.5)',
        label: 'Legendary',
    },
    collection: {
        color: '#d4af37',
        glow: 'rgba(212, 175, 55, 0.5)',
        label: 'Collection',
    },
};

function CoinBadge({ badge, size = 'md', showLabel = true, onClick }) {
    // Handle both old format (tier/emoji/name/movie) and new format (rarity/mediaTitle/coinImageUrl)
    const rarity = badge.rarity || badge.tier || 'gold';
    const isCollection = badge.sourceType === 'collection';
    const config = isCollection ? rarityConfig.collection : rarityConfig[rarity] || rarityConfig.gold;

    // Collection coins show 'Collection' as the tier label, not their underlying rarity
    const tierLabel = isCollection ? 'Collection' : config.label;

    const posterUrl = resolveCoinMedia(badge).posterUrl;

    const title = badge.mediaTitle || badge.name || badge.movie || 'Unknown';
    const seasonLabel = badge.seasonNumber ? ` S${badge.seasonNumber}` : '';

    const handleClick = () => {
        if (onClick) {
            onClick(badge);
        }
    };

    return (
        <div
            className={`${styles.wrapper} ${styles[`wrapper--${size}`]} ${onClick ? styles.clickable : ''}`}
            onClick={handleClick}
        >
            <div
                className={styles.coin}
                style={{
                    '--coin-color': config.color,
                    '--coin-glow': config.glow,
                }}
            >
                <div className={styles.coinInner}>
                    {/* Grid tile: Signature coins show their static poster here and
                        only animate on the detail view. */}
                    {posterUrl ? (
                        <img
                            src={posterUrl}
                            alt={title}
                            className={styles.coinImage}
                        />
                    ) : badge.emoji ? (
                        <span className={styles.emoji}>{badge.emoji}</span>
                    ) : (
                        <span className={styles.emoji}>🪙</span>
                    )}
                </div>
                <div className={styles.shine} />
                <SignatureBadge coin={badge} compact className={styles.signatureBadge} />
            </div>

            {showLabel && (
                <div className={styles.label}>
                    <span className={styles.name}>{title}{seasonLabel}</span>
                    {badge.movie && <span className={styles.movie}>{badge.movie}</span>}
                    <span
                        className={styles.tier}
                        style={{ color: config.color }}
                    >
                        {tierLabel}
                    </span>
                </div>
            )}
        </div>
    );
}

export default CoinBadge;